// Ruta /settings — preferencias del usuario (Sprint 8).
//
// - Idioma (ES/EN): persiste en localStorage vía `setLocale`.
// - Sonidos (on/off): persiste en localStorage vía `useSoundStore`.
// - Botón "skip to main content" para a11y (mismo link global).
//
// Es deliberadamente minimalista: un form sin librería externa.

import { createFileRoute, Link } from '@tanstack/react-router';
import { useT, setLocale, getLocale, type Locale } from '../lib/i18n';
import { useSoundStore, SoundHydrator } from '../lib/sounds';
import { usePWAInstall } from '../lib/usePWAInstall';
import { Button } from '../components/ui/Button';

export const Route = createFileRoute('/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  const t = useT();
  const enabled = useSoundStore((s) => s.enabled);
  const toggle = useSoundStore((s) => s.toggle);
  const locale = getLocale();
  const { canInstall, installed, promptInstall } = usePWAInstall();

  return (
    <main id="main-content" className="min-h-screen bg-bg text-text">
      <SoundHydrator />
      <div className="border-b border-border bg-bg-alt/40">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-3 md:px-6">
          <Link to="/" className="text-sm text-text-muted transition hover:text-gold">
            {t('common.back')}
          </Link>
          <h1 className="font-display text-lg text-gold">{t('settings.title')}</h1>
          <span className="w-12" />
        </div>
      </div>

      <section className="mx-auto max-w-3xl space-y-6 p-4 md:p-6">
        <div
          className="rounded-xl border border-border bg-bg-card p-4"
          data-testid="settings-language"
        >
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-muted">
            {t('settings.language')}
          </h2>
          <div className="flex gap-2" role="group" aria-label={t('settings.language')}>
            {(['es', 'en'] as Locale[]).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => setLocale(l)}
                aria-pressed={locale === l}
                className={[
                  'rounded-md px-3 py-2 text-sm transition',
                  locale === l
                    ? 'bg-gold text-bg'
                    : 'border border-border text-text-muted hover:border-gold hover:text-gold',
                ].join(' ')}
                data-testid={`locale-${l}`}
              >
                {l === 'es' ? 'Español' : 'English'}
              </button>
            ))}
          </div>
        </div>

        <div
          className="rounded-xl border border-border bg-bg-card p-4"
          data-testid="settings-sound"
        >
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-muted">
            {t('settings.sound')}
          </h2>
          <Button onClick={toggle} aria-pressed={enabled} data-testid="sound-toggle">
            {enabled ? t('settings.sound.on') : t('settings.sound.off')}
          </Button>
        </div>

        <div className="rounded-xl border border-border bg-bg-card p-4" data-testid="settings-pwa">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-text-muted">
            PWA
          </h2>
          {installed ? (
            <p className="text-sm text-emerald-400">✓ Instalada</p>
          ) : canInstall ? (
            <Button
              onClick={() => {
                void promptInstall();
              }}
              data-testid="pwa-install-btn"
            >
              Instalar app
            </Button>
          ) : (
            <p className="text-sm text-text-muted">No disponible en este navegador.</p>
          )}
        </div>
      </section>
    </main>
  );
}
